import { create } from "zustand";

export const THEME_STORAGE_KEY = "trekkin.theme";
export const THEME_OPTIONS = ["system", "light", "dark"];

const normalizeTheme = (value) => {
  const theme = String(value || "").toLowerCase();
  return THEME_OPTIONS.includes(theme) ? theme : "system";
};

const readStoredTheme = () => {
  try {
    return normalizeTheme(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return "system";
  }
};

const getSystemTheme = () => {
  try {
    return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches
      ? "dark"
      : "light";
  } catch {
    return "light";
  }
};

const applyThemeToDocument = (theme) => {
  if (typeof document === "undefined") return normalizeTheme(theme);

  const normalized = normalizeTheme(theme);
  const resolved = normalized === "system" ? getSystemTheme() : normalized;
  const root = document.documentElement;

  root.dataset.themePreference = normalized;
  root.dataset.theme = resolved;
  root.classList.toggle("theme-dark", resolved === "dark");
  root.classList.toggle("theme-light", resolved === "light");
  root.style.colorScheme = resolved;

  try {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute("content", resolved === "dark" ? "#0f1512" : "#f5f8f6");
    }
  } catch {}

  return resolved;
};

export const initializeTheme = () => {
  const theme = readStoredTheme();
  const resolvedTheme = applyThemeToDocument(theme);
  useThemeStore.setState({ theme, resolvedTheme });
  useThemeStore.getState()?.startSystemThemeListener?.();
  return { theme, resolvedTheme };
};

let systemThemeUnsubscribe = null;

export const useThemeStore = create((set, get) => ({
  theme: readStoredTheme(),
  resolvedTheme: "light",

  setTheme: (nextTheme) => {
    const theme = normalizeTheme(nextTheme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {}
    const resolvedTheme = applyThemeToDocument(theme);
    set({ theme, resolvedTheme });
  },

  startSystemThemeListener: () => {
    if (systemThemeUnsubscribe || typeof window === "undefined") return;
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!media) return;

    const handleChange = () => {
      const currentTheme = get().theme;
      if (currentTheme !== "system") return;
      const resolvedTheme = applyThemeToDocument(currentTheme);
      set({ resolvedTheme });
    };

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", handleChange);
      systemThemeUnsubscribe = () => media.removeEventListener("change", handleChange);
    } else if (typeof media.addListener === "function") {
      media.addListener(handleChange);
      systemThemeUnsubscribe = () => media.removeListener(handleChange);
    }

    handleChange();
  },
}));
